/* ==========================================================================
   The Mitesh — behaviour for the custom sections only.
   Cart, checkout and localization are Dawn's; nothing here touches them.
   ========================================================================== */
(() => {
  /* Reveal-on-scroll ------------------------------------------------------ */
  const initReveals = (root = document) => {
    const items = root.querySelectorAll(".reveal:not(.is-visible)");
    if (!items.length) return;

    if (!("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );

    items.forEach((el) => observer.observe(el));
  };

  /* Campaign band video ---------------------------------------------------
     Hides itself when no mp4 is present so the poster/background image shows,
     and only plays while its band is in view (the video is position:fixed,
     so the band is what we observe).                                       */
  const initCampaignBands = (root = document) => {
    const bands = [...root.querySelectorAll(".campaign-band")];
    if (!bands.length) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    bands.forEach((band) => {
      const video = band.querySelector("[data-campaign-video]");
      if (!video || video.dataset.campaignReady === "true") return;
      video.dataset.campaignReady = "true";

      const dropVideo = () => {
        video.hidden = true;
      };

      // The source's error event can fire before this deferred script runs, so
      // read networkState directly (3 === NETWORK_NO_SOURCE) as well as listening.
      const settleVideo = () => {
        if (video.networkState === 3) dropVideo();
      };

      video.querySelector("source")?.addEventListener("error", dropVideo);
      video.addEventListener("error", dropVideo);
      settleVideo();
      if (document.readyState !== "complete") {
        window.addEventListener("load", settleVideo, { once: true });
      }

      if (reduceMotion.matches) video.pause();
    });

    if (!("IntersectionObserver" in window)) return;

    const bandObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector("[data-campaign-video]");
          if (!video || video.hidden) return;
          if (entry.isIntersecting && !reduceMotion.matches) {
            video.play?.().catch(() => {
              /* autoplay refused; poster remains visible */
            });
          } else {
            video.pause?.();
          }
        });
      },
      { rootMargin: "15% 0px" }
    );

    bands.forEach((band) => bandObserver.observe(band));
  };

  /* Header ---------------------------------------------------------------
     The overlay header sits transparent over the hero and turns solid once
     the page scrolls. Dawn's own menu drawer handles mobile navigation.    */
  const initHeader = () => {
    const header = document.querySelector("[data-mitesh-header]");
    if (!header || header.dataset.miteshHeaderReady === "true") return;
    header.dataset.miteshHeaderReady = "true";

    const update = () => header.classList.toggle("is-scrolled", window.scrollY > 12);
    update();
    window.addEventListener("scroll", update, { passive: true });

    // Announcement bars vary in height; measure rather than assume.
    const setOffset = () => {
      const bar = document.querySelector(".announcement-bar, .utility-bar");
      const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty("--announcement-height", h + "px");
      if (header.classList.contains("mitesh-header--overlay")) {
        header.style.top = header.classList.contains("is-scrolled") ? "0px" : h + "px";
      }
    };
    setOffset();
    window.addEventListener("resize", setOffset, { passive: true });
    window.addEventListener("scroll", setOffset, { passive: true });

    // Mobile navigation drawer
    const drawer = document.querySelector("[data-mitesh-drawer]");
    const opener = document.querySelector("[data-mitesh-menu-open]");
    if (!drawer || !opener) return;

    let lastFocused = null;
    const setDrawer = (isOpen) => {
      drawer.setAttribute("aria-hidden", String(!isOpen));
      opener.setAttribute("aria-expanded", String(isOpen));
      document.body.classList.toggle("mitesh-menu-open", isOpen);
      if (isOpen) {
        lastFocused = document.activeElement;
        drawer.querySelector("[data-mitesh-menu-close]")?.focus();
      } else {
        lastFocused?.focus();
      }
    };

    opener.addEventListener("click", () => setDrawer(true));
    drawer.querySelectorAll("[data-mitesh-menu-close]").forEach((el) =>
      el.addEventListener("click", () => setDrawer(false))
    );
    drawer.querySelectorAll("a").forEach((a) =>
      a.addEventListener("click", () => setDrawer(false))
    );
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && drawer.getAttribute("aria-hidden") === "false") {
        setDrawer(false);
      }
    });
  };

  const init = (root) => {
    initReveals(root);
    initCampaignBands(root);
    initHeader();
  };

  // Disarms the failsafe in theme.liquid — tells it this script did load.
  document.documentElement.dataset.miteshReady = "true";

  init(document);

  // Re-run when a merchant edits a section in the theme editor.
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
